import { Button } from "@component/button";
import { InterviewPlayer } from "@component/interview-player";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import {
    interviewStatusComplete,
    interviewStatusInComplete,
    interviewStatusNone,
    interviewStatusPendingInterview,
    interviewStatusPendingVerification,
} from "@constant/app";
import { getUserInterviewSubmission } from "@service/assessment";
import { getInterviewInsights, getVideo } from "@service/interview";
import {
    AssessmentSubmissions,
    FormAssessment,
    FormQuestion,
    InterviewAssessment,
} from "@type/assessments";
import { CandidateInterviewStatus } from "@type/candidate";
import { InterviewInsight, InterviewVideo } from "@type/videoInterview";
import { formatDuration, parseFormat } from "@util/date";
import { catchErrorLog } from "@util/sentry";
import { getStatusBadgeClass } from "@util/string";
import { cn } from "@util/tailwind";
import {
    AlertCircleIcon,
    BarChart3,
    Check,
    CheckCircle2,
    CheckSquareIcon,
    ClockIcon,
    LightbulbIcon,
    MessageSquareIcon,
    ThumbsUpIcon,
    X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface Props {
    user_id: string
    assessment_id: string
    submission: AssessmentSubmissions
    isOpen: boolean;
    isAIInterview: boolean;
    assessment: InterviewAssessment | FormAssessment | undefined;
    onClose: () => void;
}

export default function DrawerAIInterview(props: Props) {
    const contentRef = useRef<HTMLDivElement>(null);
    const [videoDuration, setVideoDuration] = useState<number | null>(null);
    const [interviewStatus, setInterviewStatus] = useState<CandidateInterviewStatus | undefined>(undefined)
    const [interviewInsights, setInterviewInsights] = useState<InterviewInsight | null>(null);
    const [isLoadingInterviewInsight, setLoadingInterviewInsight] = useState<boolean>(true);
    const [isLoadingVideo, setLoadingVideo] = useState<boolean>(true);
    const [videoInterview, setVideoInterview] = useState<InterviewVideo | undefined>(undefined);

    const isFormWithScoring = !props.isAIInterview
        && (props.assessment as FormAssessment)?.content?.include_in_candidate_scoring === true;

    useEffect(() => {
        if (props.isOpen && props.isAIInterview) {
            getUserInterviewSubmission(props.assessment_id, props.user_id).then((submission) => {
                if (submission && submission.conversation_id) {
                    setInterviewStatus({
                        status: submission.status || interviewStatusNone,
                        reason: "",
                        updated_at: submission.updated_at || "",
                    })

                    getVideo(submission.conversation_id)
                        .then((resp) => {
                            if (resp) {
                                setVideoInterview(resp);
                            }
                            setLoadingVideo(false);
                        })
                        .catch((error) => {
                            catchErrorLog(`Failed to get video for conversation ID: ${submission.conversation_id}`, error);
                            setLoadingVideo(false);
                        });

                    getInterviewInsights(submission.conversation_id)
                        .then((res) => {
                            if (res?.insights) {
                                setInterviewInsights(JSON.parse(res.insights));
                            }
                            setLoadingInterviewInsight(false);
                        })
                        .catch((error) => {
                            catchErrorLog(`Failed to get interview insights for conversation ID: ${submission.conversation_id}`, error);
                            setLoadingInterviewInsight(false);
                        });
                }
            })
        }
    }, [props.isOpen])

    const hasAnswerValidation = (question: FormQuestion): boolean => {
        if (question.type === "Short Answer" && question.correct_answers && question.correct_answers.length > 0) return true;
        if (question.type === "Multiple Choice" && question.options && question.option_correct_index !== undefined && question.option_correct_index !== null) return true;
        if (question.type === "Rating Scale" && question.min_rate !== undefined && question.max_rate !== undefined) return true;
        return false;
    };

    const renderScoringExplanation = (question: FormQuestion, answer: string, summary?: string) => {
        if (!hasAnswerValidation(question)) return null;

        return (
            <div className="mt-4">
                {/* Short Answer Explanation */}
                {question.type === "Short Answer" && question.correct_answers && question.correct_answers.length > 0 && (
                    <div className="space-y-2">
                        <p className="text-sm text-gray-900">
                            {summary || "No summary provided by the candidate."}
                        </p>
                        <div className="space-y-1.5">
                            <p className="text-sm text-gray-600">Expected answers:</p>
                            <div className="flex flex-wrap items-center gap-2">
                                {question.correct_answers.map((keyword, idx) => (
                                    <span
                                        key={idx}
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium"
                                    >
                                        {keyword}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Multiple Choice Explanation */}
                {question.type === "Multiple Choice" && question.options && question.option_correct_index !== undefined && question.option_correct_index !== null && (
                    <div>
                        {answer === question.options[question.option_correct_index] ? (
                            <div className="flex items-center gap-2">
                                <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Check className="w-3 h-3 text-white" />
                                </div>
                                <p className="text-sm text-gray-700">
                                    Correct answer selected.
                                </p>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <X className="w-3 h-3 text-white" />
                                </div>
                                <p className="text-sm text-gray-700">
                                    <span className="font-semibold">Incorrect answer.</span> The correct answer is: &ldquo;{question.options[question.option_correct_index]}&rdquo;
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Rating Scale Explanation */}
                {question.type === "Rating Scale" && question.min_rate !== undefined && question.max_rate !== undefined && (
                    <div>
                        <p className="text-sm text-gray-700">
                            The candidate provided a rating of {answer} out of {question.max_rate},{" "}
                            {Number(answer) >= (question.max_rate * 0.8)
                                ? "indicating a highly positive response."
                                : Number(answer) >= (question.max_rate * 0.6)
                                    ? "indicating a moderately positive response."
                                    : Number(answer) >= (question.max_rate * 0.4)
                                        ? "indicating a negative response."
                                        : "indicating a less favorable response."}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const renderAIInterview = (
        <div className="flex-1 overflow-y-auto" ref={contentRef}>
            <div className="p-6">
                <div className="flex flex-col mb-6">
                    {/* Interview Status */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-3">
                                <CheckSquareIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                    Interview Status
                                </h3>
                            </div>
                            <span className={`px-3 py-1 ${getStatusBadgeClass(interviewStatus?.status || "")} rounded-full text-sm font-medium`}>
                                {interviewStatus?.status === interviewStatusComplete
                                    ? "Completed"
                                    : interviewStatus?.status === interviewStatusInComplete
                                        ? "Incomplete"
                                        : "Pending"}
                            </span>
                        </div>

                        {interviewStatus?.status === interviewStatusComplete && (
                            <>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    The candidate has completed their AI Interview on{" "}
                                    {parseFormat(interviewStatus.updated_at || "", "h.mmA [on] D MMMM YYYY")}.
                                </p>
                                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                    <ClockIcon className="h-4 w-4" />
                                    <span>Duration: {videoDuration ? formatDuration(videoDuration) : (
                                        <svg
                                            className="animate-spin inline ml-1 h-4 w-4 text-gray-500 dark:text-gray-400"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                    )}</span>
                                </div>
                            </>
                        )}

                        {(interviewStatus?.status === interviewStatusNone || interviewStatus?.status === interviewStatusPendingVerification) && (
                            <p className="text-sm text-gray-600">
                                The candidate is in the process of retaking their AI Interview.
                            </p>
                        )}

                        {interviewStatus?.status === interviewStatusPendingInterview && !interviewStatus?.reason && (
                            <p className="text-sm text-gray-600">
                                The candidate has not started their AI Interview.
                            </p>
                        )}

                        {interviewStatus?.status === interviewStatusInComplete && (
                            <p className="text-sm text-gray-600">
                                The candidate attempted the AI Interview on {parseFormat(interviewStatus?.updated_at || "", "D MMMM YYYY")} but did not complete.
                            </p>
                        )}
                    </div>

                    {(interviewStatus?.status === interviewStatusComplete || interviewStatus?.status === interviewStatusInComplete) && (
                        <div className="space-y-6 mb-6">
                            {/* Interview Scoring Breakdown */}
                            {(interviewInsights as InterviewInsight)?.interview_score && (interviewInsights as InterviewInsight).interview_score!.length > 0 && (
                                <div className="mt-6 border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900">
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <h4 className="font-medium text-base flex items-center">
                                                <BarChart3 className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                                Interview Scoring Breakdown
                                            </h4>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Based on weighted evaluation across {(interviewInsights as InterviewInsight).interview_score!.length}{" "}
                                                competencies.
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-muted-foreground">Total Interview Score:</span>
                                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-base font-semibold">
                                                {(interviewInsights as InterviewInsight).total_interview_score ?? 0}/100
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <div className="space-y-4">
                                            {(interviewInsights as InterviewInsight).interview_score!.map((scoreItem, index) => (
                                                <div key={index} className="space-y-1.5">
                                                    <div className="flex items-center justify-start gap-2">
                                                        <span className="text-sm font-medium">{scoreItem.title}</span>
                                                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                                            {scoreItem.score}/{scoreItem.max_score}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                                        {scoreItem.explanation}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* AI Insights Loading */}
                            {isLoadingInterviewInsight && (
                                <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900">
                                    <h4 className="font-medium text-base flex items-center mb-4">
                                        <LightbulbIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                        AI Insights & Recommendations
                                    </h4>
                                    <div className="flex items-center justify-center h-32">
                                        <svg
                                            className="animate-spin h-8 w-8 text-gray-500 dark:text-gray-400"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle className="opacity-25" cx="12" cy="12" r="10" strokeWidth="4" stroke="currentColor" />
                                            <path className="opacity-75" fill="currentColor" d="M4.93 4.93a10 10 0 0114.14 14.14L12 12z" />
                                        </svg>
                                    </div>
                                </div>
                            )}

                            {/* AI Insights & Recommendations */}
                            {!isLoadingInterviewInsight && interviewInsights && (
                                <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900">
                                    <h4 className="font-medium text-base flex items-center mb-4">
                                        <LightbulbIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                        AI Insights & Recommendations
                                    </h4>
                                    <div className="space-y-4">
                                        <div className="space-y-1.5">
                                            <div className="flex items-center">
                                                <CheckCircle2 className="h-4 w-4 mr-2 text-green-600 dark:text-green-400" />
                                                <span className="font-medium text-sm text-green-800 dark:text-green-200">
                                                    Strengths
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {interviewInsights.strengths}
                                            </p>
                                        </div>
                                        <div className="space-y-1.5">
                                            <div className="flex items-center">
                                                <AlertCircleIcon className="h-4 w-4 mr-2 text-orange-600 dark:text-orange-400" />
                                                <span className="font-medium text-sm text-orange-800 dark:text-orange-200">
                                                    Areas for Growth
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {interviewInsights.areas_for_growth}
                                            </p>
                                        </div>
                                        <div className="space-y-1.5">
                                            <div className="flex items-center">
                                                <ThumbsUpIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                                <span className="font-medium text-sm text-blue-800 dark:text-blue-200">
                                                    Overall Recommendation
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {interviewInsights.recommendation}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Key Questions & Responses */}
                    {interviewStatus?.status === interviewStatusComplete && !isLoadingInterviewInsight && interviewInsights?.key_questions_and_answers.length ? (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <MessageSquareIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                    Key Questions & Responses
                                </h3>
                            </div>
                            <div className="space-y-4">
                                {interviewInsights?.key_questions_and_answers.map((elem, idx) => (
                                    <div key={idx}>
                                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                                            Q: {elem.question}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                                            {elem.answer}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>

                <InterviewPlayer
                    isLoadingVideo={isLoadingVideo}
                    interviewStatus={interviewStatus}
                    s3VideoLink={interviewStatus?.status === interviewStatusComplete ? videoInterview?.s3_link || "" : null}
                    videoInterview={interviewStatus?.status === interviewStatusComplete ? videoInterview as InterviewVideo : {} as InterviewVideo}
                    setVideoDuration={(newDuration: number) => interviewStatus?.status === interviewStatusComplete ? setVideoDuration(newDuration) : () => console.log("video not loaded")}
                />
            </div>
        </div>
    )

    const getQuestions = (): FormQuestion[] => {
        if ((props.submission.questions || []).length > 0) {
            return props.submission.questions || [];
        }
        return (props.assessment as FormAssessment)?.content?.questions || [];
    };


    const renderFormSubmission = (
        <div className="flex-1 overflow-y-auto" ref={contentRef}>
            <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-base font-semibold">
                        Questions ({getQuestions().length})
                    </h2>
                    {isFormWithScoring && props.submission.score !== "" && (
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Total Form Score:</span>
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-base font-semibold">
                                {props.submission.score}
                            </span>
                        </div>
                    )}
                </div>

                <div className="space-y-4">
                    {getQuestions().map((question, index) => {
                        const answerData = props.submission.answers.find(ans => ans.id === question.id);
                        const answer = answerData?.content || "";
                        return (
                            <div
                                key={question.id}
                                className="border border-gray-300 rounded-lg p-4"
                                style={{ border: "0.909091px solid #d1d5db" }}
                            >
                                {/* Question Header */}
                                <div
                                    className="flex items-center justify-between mb-4"
                                    style={{
                                        paddingTop: "8px",
                                        paddingBottom: "0px",
                                        paddingLeft: "16px",
                                        paddingRight: "16px",
                                    }}
                                >
                                    <div className="flex items-center gap-4">
                                        <div
                                            className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full text-sm font-medium"
                                            style={{ border: "1px solid #bfdbfe" }}
                                        >
                                            {index + 1}
                                        </div>
                                        <span className="text-sm font-semibold text-gray-700">
                                            {question.type}
                                        </span>
                                    </div>
                                    {/* Show points if question has points */}
                                    {question.points && question.points !== undefined && (
                                        <div className="text-base font-semibold text-blue-600 dark:text-blue-400">
                                            <span>{answerData?.point}/{question.points} {question.points === 1 ? 'point' : 'points'}</span>
                                        </div>
                                    )}
                                </div>
                                {/* Question Content */}
                                <div
                                    className="space-y-4"
                                    style={{ paddingTop: "0px" }}
                                >
                                    <div
                                        className="space-y-4"
                                        style={{
                                            paddingBottom: "8px",
                                            paddingRight: "16px",
                                            paddingLeft: "16px",
                                        }}
                                    >
                                        <div
                                            className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                                            style={{
                                                backgroundColor: "transparent",
                                                border: "0px solid rgb(228, 228, 231)",
                                                paddingTop: "8px",
                                                paddingBottom: "8px",
                                                paddingRight: "0px",
                                                paddingLeft: "0px",
                                            }}
                                        >
                                            <div
                                                className="text-sm text-gray-700"
                                                dangerouslySetInnerHTML={{ __html: question.content }}
                                            />
                                        </div>

                                        {/* Candidate Response */}
                                        <div className="mt-4">
                                            <label className="text-sm font-semibold text-gray-700 block mb-2">
                                                Candidate Response:
                                            </label>
                                            {question.type === "Rating Scale" ? (
                                                <div className="space-y-4">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <span className="text-sm text-gray-600 dark:text-gray-400 text-right mr-2">
                                                            {question.min_label}
                                                        </span>
                                                        {Array.from({ length: (question.max_rate || 5) - (question.min_rate || 1) + 1 }, (_, i) => (question.min_rate || 1) + i).map((value) => (
                                                            <button
                                                                key={value}
                                                                type="button"
                                                                disabled
                                                                className={cn(
                                                                    "w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-medium transition-all",
                                                                    value === parseInt(answer)
                                                                        ? "bg-blue-600 border-blue-600 text-white"
                                                                        : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                                                                )}
                                                            >
                                                                {value}
                                                            </button>
                                                        ))}
                                                        <span className="text-sm text-gray-600 dark:text-gray-400 text-left ml-2">
                                                            {question.max_label}
                                                        </span>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                                            Selected: {answer}
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : question.type === "Multiple Choice" ? (
                                                <Select
                                                    value={answer || undefined}
                                                    disabled
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue placeholder="Select an option" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {(question.options || []).map((option, idx) => (
                                                            <SelectItem key={idx} value={option}>
                                                                {option}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                        <div className="bg-white border border-gray-200 rounded-md py-1.5 px-2 min-h-[36px]">
                                                    {answer ? (
                                                        <p className="text-sm text-gray-900">
                                                            {answer}
                                                        </p>
                                                    ) : (
                                                        <p className="text-sm text-gray-400 italic">
                                                            No response provided
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Scoring Explanation */}
                                        {renderScoringExplanation(question, answer, answerData?.summary)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    )

    return (
        <div
            className={`h-screen ${props.isOpen ? "block" : "hidden"} bg-white flex flex-col border-l border-gray-200`}
        >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                            <h1 className="text-lg font-semibold text-gray-900">
                                Responses from {props.submission.jobseeker_name}
                            </h1>
                        </div>
                        <div className="flex items-center gap-4">
                            <p className="text-sm text-gray-600">
                                Submitted on {parseFormat(props.submission.updated_at, "D MMM YYYY h:mm A")}
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={props.onClose}
                        className="p-2 hover:bg-gray-100 mt-1"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Content */}
            {props.isAIInterview ? renderAIInterview : renderFormSubmission}
        </div>
    )
}
